import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { SeoHead } from '../components/SeoHead';
import { ChevronLeft, Calendar, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
}

function readMinutes(body) {
  const words = (body || '').trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 220));
}

/**
 * Single article view at /articles/:slug.
 * Renders markdown body, ships Article JSON-LD, and links back to the index.
 */
export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/articles/${slug}`);
        if (res.ok) {
          setArticle(await res.json());
        } else {
          setNotFound(true);
        }
      } catch {
        setNotFound(true);
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-rooam-black text-white flex items-center justify-center">
        <p className="text-zinc-500 text-sm">Loading…</p>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-rooam-black text-white">
        <SeoHead title="Article not found" path={`/articles/${slug}`} noindex />
        <main className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-black mb-3">Article not found</h1>
          <p className="text-zinc-400 mb-6">This article may have been moved or unpublished.</p>
          <button onClick={() => navigate('/articles')} className="text-red-400 hover:text-red-300 font-semibold" data-testid="article-back-to-list">
            ← Back to all articles
          </button>
        </main>
      </div>
    );
  }

  const mins = readMinutes(article.body);

  return (
    <div className="min-h-screen bg-rooam-black text-white">
      <SeoHead
        title={article.title}
        description={article.seo_description || article.subtitle || article.title}
        path={`/articles/${article.slug}`}
        image={article.cover_image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": article.title,
          "description": article.seo_description || article.subtitle,
          "image": article.cover_image
            ? (article.cover_image.startsWith('http') ? article.cover_image : `https://rooamsports.network${article.cover_image}`)
            : 'https://rooamsports.network/og-image.png',
          "author": { "@type": "Organization", "name": article.author || "Rooam Sports Network" },
          "publisher": {
            "@type": "Organization",
            "name": "Rooam Sports Network",
            "logo": { "@type": "ImageObject", "url": "https://rooamsports.network/rooam-logo.png" }
          },
          "datePublished": article.published_at,
          "dateModified": article.updated_at || article.published_at,
          "mainEntityOfPage": `https://rooamsports.network/articles/${article.slug}`
        }}
      />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <Link to="/articles" className="inline-flex items-center text-zinc-500 hover:text-white text-sm mb-6 transition-colors" data-testid="article-back">
          <ChevronLeft className="w-4 h-4 mr-1" />
          All articles
        </Link>

        <article>
          {(article.tags || []).slice(0, 1).map(t => (
            <span key={t} className="inline-block text-[10px] uppercase tracking-wider font-bold text-red-400 mb-3" data-testid={`article-tag-${t}`}>
              {t}
            </span>
          ))}

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-4" style={{ fontFamily: 'Oswald, sans-serif' }}>
            {article.title}
          </h1>

          {article.subtitle && (
            <p className="text-lg sm:text-xl text-zinc-400 leading-relaxed mb-6">{article.subtitle}</p>
          )}

          <div className="flex items-center gap-3 text-xs text-zinc-500 border-b border-zinc-800 pb-6 mb-8">
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(article.published_at)}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{mins} min read</span>
            <span>·</span>
            <span>{article.author}</span>
          </div>

          {article.cover_image && (
            <div className="aspect-[2/1] bg-black rounded-xl overflow-hidden mb-8 border border-zinc-800">
              <img src={article.cover_image} alt="" className="w-full h-full object-cover" />
            </div>
          )}

          <div className="prose prose-invert prose-zinc max-w-none article-body">
            <ReactMarkdown>{article.body}</ReactMarkdown>
          </div>
        </article>

        {/* CTA at the bottom */}
        <div className="mt-14 pt-8 border-t border-zinc-800">
          <p className="text-zinc-400 text-sm mb-4">More from the Rooam Newsroom →</p>
          <Link to="/articles" className="inline-flex items-center px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm font-semibold transition-colors" data-testid="article-back-cta">
            See all articles
          </Link>
        </div>
      </main>
    </div>
  );
}
